import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardActions from '@mui/material/CardActions'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Link from '@mui/material/Link'
import Chip from '@mui/material/Chip'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'

export interface ResearchEntry {
  id: string
  title: string
  authors: string
  year: number | null
  main_theme: string
  subthemes: string
  summary: string
  source: string
}

function truncate(text: string, max = 220) {
  if (!text || text.length <= max) return text
  const cut = text.lastIndexOf(' ', max)
  return text.slice(0, cut > 0 ? cut : max) + '…'
}

function sourceDomain(url: string) {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}

interface ResearchCardProps {
  entry: ResearchEntry
  featured?: boolean
}

export default function ResearchCard({ entry, featured = false }: ResearchCardProps) {
  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: featured ? 'secondary.main' : 'rgba(11, 32, 39, 0.08)',
        borderTop: '3px solid',
        borderTopColor: featured ? 'secondary.main' : 'rgba(11, 32, 39, 0.1)',
        borderRadius: 3,
        backgroundColor: featured ? 'rgba(184, 115, 51, 0.04)' : 'background.paper',
        boxShadow: featured
          ? '0 14px 30px rgba(184, 115, 51, 0.12)'
          : '0 6px 18px rgba(11, 32, 39, 0.05)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          boxShadow: featured
            ? '0 18px 36px rgba(184, 115, 51, 0.18)'
            : '0 14px 28px rgba(11, 32, 39, 0.1)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <CardContent sx={{ flex: 1, p: 3, pb: 1.5 }}>
        {entry.subthemes && (
          <Chip
            label={entry.subthemes}
            size="small"
            sx={{
              mb: 1.5,
              fontSize: '0.7rem',
              height: 22,
              backgroundColor: 'rgba(138, 154, 91, 0.1)',
              color: 'primary.dark',
              border: '1px solid rgba(138, 154, 91, 0.2)',
            }}
          />
        )}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            fontSize: '0.95rem',
            lineHeight: 1.4,
            mb: 1.5,
            color: 'text.primary',
          }}
        >
          {entry.title}
        </Typography>
        {entry.summary && (
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', lineHeight: 1.65, mb: 1.5 }}
          >
            {truncate(entry.summary)}
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          {entry.year && (
            <Typography variant="caption" sx={{ color: 'text.disabled', mr: 0.5 }}>
              {entry.year}
            </Typography>
          )}
          {entry.authors && (
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              {entry.authors.length > 60
                ? entry.authors.substring(0, 60) + '…'
                : entry.authors}
            </Typography>
          )}
        </Box>
      </CardContent>
      <CardActions sx={{ px: 3, pb: 2.5, pt: 0.5 }}>
        <Link
          href={entry.source}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: 'primary.main',
            fontSize: '0.78rem',
            fontWeight: 600,
          }}
        >
          {sourceDomain(entry.source)} <OpenInNewIcon sx={{ fontSize: 12 }} />
        </Link>
      </CardActions>
    </Card>
  )
}
